// tilePaging.js — #2224 "User tiles should resize to accommodate X number of
// tiles on screen".
//
// The grid ALREADY dynamically resizes by participant count (functions/
// tileGridLayout.js applyTileGridLayout computes cols/rows from `n`) — what
// was missing per the GH acceptance criteria ("the window session should
// have a number of tiles settable to be viewed per screen") is a
// user-settable CAP + pagination once a call exceeds it. `null` (the
// default) means "no cap" — the existing unbounded auto-fit behavior is
// unchanged for every call under the cap, and for every call at all until a
// user opts in.
//
// State:
//   root._meetTilesPerScreen — number|null. null = unlimited (default).
//   root._meetTilePage       — 0-based current page index.
//
// Pure helpers are exported (and unit-tested) separately from the `this`-bound
// el.call handlers so the paging MATH has no framework dependency.

export const TILES_PER_SCREEN_OPTIONS = [
  { value: null, label: 'meet.tiles.auto' },
  { value: 4, label: '4' },
  { value: 9, label: '9' },
  { value: 16, label: '16' }
]

// Total page count for `total` tiles at `cap` per page. A null/falsy cap (or
// a cap ≥ total) is always exactly one page.
export const tilePageCount = (total, cap) => {
  const t = Math.max(0, total | 0)
  if (!cap || cap <= 0 || cap >= t) return 1
  return Math.max(1, Math.ceil(t / cap))
}

// Clamp a candidate page index into [0, pageCount-1] — the guard every
// setter below runs through so a stale page can never point past the end
// (e.g. the cap tightened, or people left the call since the page changed).
export const clampTilePage = (page, total, cap) => {
  const count = tilePageCount(total, cap)
  const p = Number.isFinite(page) ? Math.trunc(page) : 0
  return Math.min(Math.max(0, p), count - 1)
}

// The slice of `list` (already sorted by prominence — see VideoRoom.js
// TileGrid children) visible on `page` at `cap` tiles/page. A null/falsy cap
// returns the whole list untouched (current unbounded behavior).
export const paginateTiles = (list, page, cap) => {
  const arr = Array.isArray(list) ? list : []
  if (!cap || cap <= 0) return arr
  const p = clampTilePage(page, arr.length, cap)
  return arr.slice(p * cap, p * cap + cap)
}

// ── el.call handlers (`this.state.root` bound) ─────────────────────────────
//
// EVERY writer below re-applies the imperative grid layout after its
// root.update. The TileGrid's gridTemplateColumns/Rows are NOT reactive —
// functions/tileGridLayout.js owns them imperatively (see the header there
// for the three v3.14 reasons), and it is only invoked from the LiveKit
// participant/speaker events plus the pin/collapse/layout-mode writers. A
// cap or page change moves the RENDERED tile count without touching either,
// so the template went stale: capping a 10-person call to 4 tiles left the
// grid at `repeat(4, …) × repeat(3, …)` — the four surviving tiles kept a
// third of the height in the top row with two empty rows underneath, i.e.
// the exact "tiles don't resize for X tiles on screen" symptom #2224 asks
// us to fix. Reproduced 2026-08-08 with a stubbed TileGrid node before this
// call was added; covered by the regression tests in tilePaging.test.js.
//
// Dispatched via `this.call` (never a direct import): tileGridLayout.js
// already imports paginateTiles from THIS file, so a static import back
// would close an ESM cycle — and the framework rule is el.call anyway.

// Set the per-screen tile cap (any value from TILES_PER_SCREEN_OPTIONS, or a
// raw number/null). Resets to page 0 — a cap change invalidates whatever
// page the caller was on.
export const setTilesPerScreen = function (cap) {
  const root = this.state.root
  const value = cap == null ? null : Number(cap) || null
  root.update({ _meetTilesPerScreen: value, _meetTilePage: 0 })
  this.call('applyTileGridLayout', root)
}

export const nextTilePage = function () {
  const root = this.state.root
  const total = (root.livekitParticipants || []).length
  const cap = root._meetTilesPerScreen
  const next = clampTilePage((root._meetTilePage || 0) + 1, total, cap)
  root.update({ _meetTilePage: next })
  this.call('applyTileGridLayout', root)
}

export const prevTilePage = function () {
  const root = this.state.root
  const total = (root.livekitParticipants || []).length
  const cap = root._meetTilesPerScreen
  const prev = clampTilePage((root._meetTilePage || 0) - 1, total, cap)
  root.update({ _meetTilePage: prev })
  this.call('applyTileGridLayout', root)
}
